import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useSelectedLocation } from '../../hooks/useSelectedLocation';
import { useNotification } from '../../context/NotificationContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import './Flyers.css';

function formatDate(strDate) {
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  const date = new Date(strDate);
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear().toString().substr(-2);

  return `${day} ${month} ${year}`;
}

// Icons as inline SVGs
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const FileIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14,2 14,8 20,8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10,9 9,9 8,9" />
  </svg>
);

function Flyers() {
  const [flyers, setFlyers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [flyerToDelete, setFlyerToDelete] = useState(null);
  const { getAccessTokenSilently } = useAuth0();
  const selectedLocation = useSelectedLocation();
  const notification = useNotification();

  const navigate = useNavigate();

  const handleCreateNew = () => {
    navigate('/addflyer');
  };

  const handleEditClick = (flyer) => {
    navigate('/addflyer', { state: { flyer: flyer } });
  };

  const handleDeleteClick = (flyer) => {
    setFlyerToDelete(flyer);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!flyerToDelete) return;

    const flyerId = flyerToDelete._id || flyerToDelete.flyer_id;
    setDeleteDialogOpen(false);

    try {
      const token = await getAccessTokenSilently();
      await axios.delete(`${process.env.REACT_APP_SERVER_DOMAIN}/api/flyers/${flyerId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      notification.success('Flyer was successfully deleted.');
      setFlyers(prevFlyers => prevFlyers.filter(flyer => (flyer._id || flyer.flyer_id) !== flyerId));
    } catch (error) {
      console.error("Error deleting flyer", error);
      notification.error('Error occurred while deleting the flyer.');
    }
    setFlyerToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFlyerToDelete(null);
  };

  useEffect(() => {
    const fetchFlyers = async () => {
      if (!selectedLocation) return;

      setIsLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${process.env.REACT_APP_SERVER_DOMAIN}/api/flyers/location/${selectedLocation}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setFlyers(response.data);
      } catch (error) {
        console.error("Error fetching flyers", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFlyers();
  }, [selectedLocation, getAccessTokenSilently]);

  const sortedFlyers = [...flyers].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="flyers-page">
      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete this flyer?"
        description="This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Header */}
      <div className="flyers-header">
        <div className="flyers-header-content">
          <div className="flyers-header-left">
            <h1 className="flyers-title">Flyers</h1>
            <p className="flyers-count">
              {flyers.length} {flyers.length === 1 ? 'flyer' : 'flyers'}
            </p>
          </div>

          <button className="create-flyer-btn" onClick={handleCreateNew}>
            <PlusIcon />
            <span>Create New Flyer</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flyers-content">
        {isLoading ? (
          <div className="flyers-loading">
            <div className="loading-spinner"></div>
            <p>Loading flyers...</p>
          </div>
        ) : flyers.length === 0 ? (
          <div className="flyers-empty">
            <div className="empty-icon-wrapper">
              <FileIcon />
            </div>
            <h3>No flyers yet</h3>
            <p>Create your first flyer to get started</p>
            <button className="create-flyer-btn secondary" onClick={handleCreateNew}>
              <PlusIcon />
              <span>Create New Flyer</span>
            </button>
          </div>
        ) : (
          <div className="flyers-grid">
            {sortedFlyers.map((flyer) => (
              <div key={flyer._id || flyer.flyer_id} className="flyer-card">
                {/* Card Content */}
                <div className="flyer-card-content">
                  <div className="flyer-icon">
                    <FileIcon />
                  </div>
                  <h3 className="flyer-title">
                    {flyer.flyer_title || 'Untitled Flyer'}
                    {flyer.pdf_url && (
                      <span style={{
                        marginLeft: '8px',
                        padding: '2px 8px',
                        fontSize: '11px',
                        fontWeight: 500,
                        color: '#166534',
                        backgroundColor: '#dcfce7',
                        borderRadius: '10px',
                        verticalAlign: 'middle',
                      }}>Published</span>
                    )}
                  </h3>
                  <div className="flyer-dates">
                    <div className="flyer-date-row">
                      <CalendarIcon />
                      <span>{formatDate(flyer.start_date)} - {formatDate(flyer.end_date)}</span>
                    </div>
                  </div>
                </div>

                {/* Hover Actions */}
                <div className="flyer-actions-overlay">
                  <button
                    className="flyer-action-btn edit"
                    onClick={() => handleEditClick(flyer)}
                  >
                    <EditIcon />
                    <span>Edit</span>
                  </button>
                  <button
                    className="flyer-action-btn delete"
                    onClick={() => handleDeleteClick(flyer)}
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Flyers;
